import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import React, {
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useTheme } from '@/app/ThemeContext';
import { Container, DashedTag, TagSeverity } from '@/components/chariot-ui';
import { Tabs } from '@/components/chariot-ui';
import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { Entitled } from '@/components/Entitled';
import { createVulnerabilityExportConfig } from '@/components/export/configs/vulnerability.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { InputText } from '@/components/form/InputText';
import { RisksIcon } from '@/components/icons';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import { NoteModal } from '@/components/modals/NoteModal/NoteModal';
import { PushToPlexTracModal } from '@/components/reporting/PushToPlexTracModal';
import { Table } from '@/components/table/Table';
import { Column } from '@/components/table/types';
import TabsKeyboard from '@/components/Tabs';
import { Timeline } from '@/components/Timeline';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { generateSeverityOptions } from '@/features/integrations/utils/severityOptions';
import {
  fetchTicket,
  useAssociateTicket,
  useCreateTicket,
  useDisconnectTicket,
} from '@/hooks/ticket';
import { useUserSettings } from '@/hooks/useAccounts';
import { useQueryAssets } from '@/hooks/useAssets';
import { useAxios } from '@/hooks/useAxios';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useEvidenceFiles } from '@/hooks/useEvidenceFiles';
import { useGetFile, useUploadFile } from '@/hooks/useFiles';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import type { PlextracDefinition } from '@/hooks/usePlextrac';
import { useSyncPlextrac } from '@/hooks/usePlextrac';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useRiskActions } from '@/hooks/useRiskActions';
import {
  useRefreshVisited,
  useReportRisk,
  useUpdateRisk,
  useUpdateRiskPublicWellBeing,
} from '@/hooks/useRisks';
import {
  getTicketingIntegration,
  useTicketingAccounts,
} from '@/hooks/useTicketingIntegrations';
import { queryClient } from '@/queryclient';
import { AddTag, SimpleTags } from '@/sections/AddTag';
import { InsufficientPermissionsTooltip } from '@/sections/Integrations';
import { NotesTab } from '@/sections/NotesTab';
import { POESelector } from '@/sections/POESelector';
import SetupModal from '@/sections/SetupModal';
import {
  CVSSComponent,
  EPSSComponent,
  ExploitsTimelineComponent,
  FrameworkRefsComponent,
  MitreAttacksComponent,
  ThreatActorsComponent,
} from '@/sections/threats/drawer';
import type { EnrichedVulnerability } from '@/sections/threats/types';
import { VulnerabilityActions } from '@/sections/vulnerabilities/components/actions';
import { ComplianceFrameworksPanel } from '@/sections/vulnerabilities/components/ComplianceFrameworksPanel';
import { CorrelationGraph } from '@/sections/vulnerabilities/components/CorrelationGraph';
import { JiraTicketActionsMenu } from '@/sections/vulnerabilities/components/JiraTicketActionsMenu';
import { SsvcDecisionTree } from '@/sections/vulnerabilities/components/SsvcDecisionTree';
import { AssetsTab } from '@/sections/vulnerabilities/components/tabs/AssetsTab';
import { getAttackSurfaceChangeOptions } from '@/sections/vulnerabilities/config/actionOptions/attackSurface';
import { usePatchAndPR } from '@/sections/vulnerabilities/hooks/actions/usePatchAndPR';
import {
  useVulnerabilityDrawerData,
  useVulnerabilityDrawerTab,
} from '@/sections/vulnerabilities/hooks/drawer';
import {
  EmbeddedTicket,
  getTicketKey,
  useOptimisticTickets,
} from '@/sections/vulnerabilities/hooks/useOptimisticTickets';
import type { RiskFrameworkEnrichment } from '@/sections/vulnerabilities/types';
import { injectAffectedAssetsSection } from '@/sections/vulnerabilities/utils/affectedAssetsSection.util';
import {
  parseRiskFrameworkEnrichment,
  riskEnrichmentToFrameworkRefs,
  riskFrameworkS3Key,
} from '@/sections/vulnerabilities/utils/frameworkEnrichment.util';
import { filterInternalTags } from '@/utils/userDisplay.util';
// Lazy-load to avoid pulling in heavy dependencies (jszip, xlsx, papaparse) at startup
const ImportVulnerabilitiesModal = lazy(() =>
  import(
    '@/sections/vulnerabilities/components/modals/ImportVulnerabilitiesModal'
  ).then(module => ({ default: module.ImportVulnerabilitiesModal }))
);
import { HackerOneReportPanel } from '@/sections/vulnerabilities/components/HackerOneReportPanel';
import { VulnerabilityRemoveTag } from '@/sections/vulnerabilities/components/modals/RemoveTagModal';
import {
  MLPrediction,
  VulnerabilityDrawerHeader,
} from '@/sections/vulnerabilities/components/VulnerabilityDrawerHeader';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';
import {
  Account,
  Asset,
  JiraTemplate,
  ResourceType,
  Risk,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
  SeverityDef,
} from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';
import { mToMs } from '@/utils/date.util';
import { getDisplayIdentifierWithFallback } from '@/utils/displayIdentifier.util';
import { getFileString } from '@/utils/file.util';
import { getHistoryDiff } from '@/utils/getHistoryDiff.util';
import { isPraetorianCreatedRisk } from '@/utils/risk.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { getPastTense } from '@/utils/text.util';
import { getTicketDisplayId } from '@/utils/ticketDisplay.util';
import { isValidExternalURL } from '@/utils/url.util';

const relatedTicketsWidth = 284;

// VulnerabilityDetailsProps - No longer needed since component fetches its own data
// Kept for backward compatibility in case other components use it
interface VulnerabilityDetailsProps {
  risk?: Risk | null; // Made optional - component fetches data internally via key
}

const severityFilters = [
  {
    id: 'C',
    label: SeverityDef[RiskSeverity.Critical],
    value: 'C',
    severity: 'critical' as TagSeverity,
  },
  {
    id: 'H',
    label: SeverityDef[RiskSeverity.High],
    value: 'H',
    severity: 'high' as TagSeverity,
  },
  {
    id: 'M',
    label: SeverityDef[RiskSeverity.Medium],
    value: 'M',
    severity: 'medium' as TagSeverity,
  },
  {
    id: 'L',
    label: SeverityDef[RiskSeverity.Low],
    value: 'L',
    severity: 'low' as TagSeverity,
  },
  {
    id: 'I',
    label: SeverityDef[RiskSeverity.Info],
    value: 'I',
    severity: 'info' as TagSeverity,
  },
  {
    id: 'E',
    label: SeverityDef[RiskSeverity.Exposure],
    value: RiskSeverity.Exposure,
    severity: 'exposure' as TagSeverity,
  },
];

const severityMap: Record<RiskSeverity, TagSeverity> = {
  C: 'critical',
  H: 'high',
  M: 'medium',
  L: 'low',
  I: 'info',
  E: 'exposure',
};

// Leading Title / Risk ID metadata preamble stripped from definition prose
// (ENG-5067 / ENG-5075). Module-scope + stateless (no /g flag → no lastIndex
// carry-over), so it is allocated once and reused across the ~5 calls per render
// (the three hasValid*Content gates, renderOverviewContent, resolveVerificationFallback).
// - alternation handles either header order; applied twice to strip both tiers;
// - leading (?:[ \t]*\r?\n)* consumes any fully-blank leading lines, and
//   the pre-#### guard is SPACES-ONLY  {0,3} — the ≤3 spaces a markdown ATX
//   heading may legally carry — NEVER the 4+-space indent of an indented code
//   block (ENG-5075: the old ^\s* ate that indent and deleted the code line as
//   if it were metadata). It is spaces-only, not [ \t]{0,3}, precisely because a
//   leading TAB reaches CommonMark column 4 (an indented code block, not a
//   heading), so a tab-indented "\t#### Title" must be preserved as code — never
//   stripped as metadata (ENG-5075 round-4 Codex P1, comment 3619218381). The
//   leading blank-line matcher stays tab-tolerant: a "\t\n" line is genuinely
//   blank (whitespace-only) and correctly consumed;
// - the heading→value terminator is [ \t]*(?:\r?\n|$), NOT \s*\n+: the value is
//   only the line IMMEDIATELY following the heading. \s*\n+ greedily hopped a
//   blank line and captured the first line of real prose as the value, deleting
//   it (ENG-5075 round-4: "#### Title\n\nThe real description." stripped to '').
//   [ \t]*(?:\r?\n|$) ends the heading at its own line break (or EOF), so when a
//   blank line follows there is no value and the following prose is preserved;
// - trailing (?:\r?\n)* consumes only newlines, never the following prose's indent;
// - the * (not +) still fully strips a metadata-only tier at EOF-without-newline;
// - ^ string-start anchor (no /m flag) preserves genuine mid-document headings.
const DEFINITION_METADATA_PREAMBLE =
  /^(?:[ \t]*\r?\n)* {0,3}####\s*(?:Risk\s*ID|Title)[ \t]*(?:\r?\n|$)[^\n]*(?:\r?\n)*/i;

// Strip metadata sections (#### Risk ID / #### Title) the backend extraction
// regex (risk.go) never treats as content — Title comes from Risk.Name and Risk
// ID is not displayed. Shared by the Overview Description render and the
// Verification-tab fallback (ENG-5067) so both surface identical prose.
// eslint-disable-next-line react-refresh/only-export-components
export const stripDefinitionMetadata = (source: string): string =>
  source
    .replace(DEFINITION_METADATA_PREAMBLE, '')
    .replace(DEFINITION_METADATA_PREAMBLE, '');

// Resolve the Verification-tab "Analysis" fallback prose from the three
// definition tiers, mirroring the Overview Description precedence exactly
// (Instance > Account > Global > none — see renderOverviewContent). Returns the
// first non-blank tier with metadata stripped, or '' when every tier is empty
// (POE then keeps its own empty-state card). ENG-5068 added the Global tier so a
// finding whose write-up lives only in the shared canonical Praetorian definition
// still surfaces in Verification, not just Overview. Exported for unit testing.
// eslint-disable-next-line react-refresh/only-export-components
export const resolveVerificationFallback = (
  instanceValue: string,
  accountValue: string,
  globalValue: string
): string =>
  [instanceValue, accountValue, globalValue]
    .map(stripDefinitionMetadata)
    .find(v => v.trim().length > 0) ?? '';

// eslint-disable-next-line react-refresh/only-export-components
export const getRiskTagSeverity = (status: string): TagSeverity => {
  const { severity } = getRiskStatusLabel(status);

  return severityMap[severity ?? RiskSeverity.Info];
};

// eslint-disable-next-line react-refresh/only-export-components
export const getRiskStatus = (status: string): string => {
  const { status: statusLabel } = getRiskStatusLabel(status);

  return getPastTense(RiskStatusLabel[statusLabel]);
};

const sharedTabOptions = [
  {
    label: 'Overview',
    value: 'overview',
  },
  {
    label: 'Assets Impacted',
    value: 'assets',
  },
  {
    label: 'Evidence',
    value: 'evidence',
  },
];

function getTabOptions(risk: Risk | null, hasHackerOneReport: boolean) {
  const tabs = [
    ...sharedTabOptions,
    { label: 'History', value: 'history' },
    { label: 'Notes', value: 'notes' },
  ];

  if (hasHackerOneReport) {
    tabs.push({ label: 'HackerOne', value: 'hackerone' as const });
  }

  return tabs;
}

// Helper function to parse ML prediction from history
function getMLPredictionFromHistory(
  risk: Risk | null
): MLPrediction | undefined {
  // Add null check to prevent crashes when risk is not loaded yet
  if (!risk || !risk.history) return undefined;

  // Look through history in reverse to get latest ML prediction
  const mlHistory = [...(risk.history || [])]
    .reverse()
    .find(
      h =>
        h.comment?.startsWith('ML Model Prediction:') &&
        typeof h.logit === 'number'
    );

  if (!mlHistory) return undefined;

  return {
    recommendation: mlHistory.comment.split(': ')[1], // Extract O or D
    logit: mlHistory.logit!,
    model: mlHistory.Base!,
    timestamp: mlHistory.updated,
  };
}

/**
 * VulnerabilityDrawerComponent
 *
 * KEY-BASED PATTERN: Uses vulnerabilityDrawerKey from URL params as the single
 * source of truth. The drawer fetches its own data internally via
 * useVulnerabilityDrawerData(key), eliminating race conditions from dual state sources.
 */
export const VulnerabilityDrawerComponent: React.FC<
  VulnerabilityDetailsProps
> = () => {
  // KEY-BASED PATTERN: Read key from URL params via drawerState
  const {
    drawerState: { vulnerabilityDrawerKey, handleOpenVulnerabilityDrawer },
    poeMode,
  } = useGlobalState();

  // OSINT partition is handled automatically via OsintContext — when the drawer
  // is wrapped in OsintProvider (via MaybeOsint in AppDrawers), all hooks
  // (useMy, useAxios, etc.) automatically use the OSINT partition.
  const { isOsint } = useOsintContext();
  const { canWriteAssets } = useEntitlements();

  // LOCAL STATE PATTERN: Tab state managed locally to prevent race conditions
  // when arrow key navigation rapidly updates URL params
  const [currentTab, setCurrentTab] = useVulnerabilityDrawerTab();

  // Fetch data internally using the key (single source of truth)
  const {
    vulnerability: risk,
    vulnerabilityId,
    vulnerabilityTitle,
    isLoading,
  } = useVulnerabilityDrawerData(vulnerabilityDrawerKey || '');

  const { isPraetorianUser } = useAuth();

  // Lifted from VulnerabilityActions so the ConstantineProgressPanel can
  // render in the drawer body while the trigger button stays in the action bar.
  const { triggerPatchAndPR, isLoading: isPatchAndPRLoading } = usePatchAndPR();

  const maskingUserType = useMaskingUserType();
  const axios = useAxios();
  const exportConfig = useMemo(() => createVulnerabilityExportConfig(), []);
  const attributesFilter = risk ? `source:#risk#${risk.dns}#${risk.name}` : '';
  const { data: attributesGenericSearch, refetch } = useGenericSearch(
    {
      query: attributesFilter,
      exact: true,
      label: ResourceType.Attribute,
    },
    {
      // Disable ticket attribute queries in OSINT mode — OSINT risks don't have tickets
      enabled: !!risk && !isOsint,
    }
  );
  const hasHackerOneReport = (attributesGenericSearch?.attributes ?? []).some(
    attr => attr.name === 'hackerone-report-id'
  );
  const computedTabOptions = useMemo(
    () => getTabOptions(risk, hasHackerOneReport),
    [risk, hasHackerOneReport]
  );
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [isPlexTracModalOpen, setIsPlexTracModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isEditingAuxiliaryInfo, setIsEditingAuxiliaryInfo] = useState(false);
  const [auxiliaryInfoValue, setAuxiliaryInfoValue] = useState('');

  const {
    existingWriteup,
    hasExistingWriteup,
    mutate: syncWithPlextrac,
  } = useSyncPlextrac(risk?.name || '', risk?.key || '');

  const {
    data: definitionsFile,
    isFetching: isDefinitionsFileFetching,
    refetch: refetchDefinitionsFile,
  } = useGetFile(
    {
      name: `definitions/${risk?.name}`,
    },
    { enabled: !!risk }
  );

  const definitionsFileValue = getFileString(definitionsFile);

  // Per-instance overview file (definitions/{risk.key})
  const { data: instanceOverviewFile } = useGetFile(
    {
      name: `definitions/${risk?.key}`,
    },
    { enabled: !!risk }
  );

  const instanceOverviewValue = getFileString(instanceOverviewFile);

  // Global (shared canonical Praetorian) definition file (definitions/{risk.name},
  // global: true). Same query key + options as the OverviewTab child's fetch, so
  // TanStack Query dedupes to a single request — this hoists read access into the
  // parent scope, where the Verification fallback is computed (ENG-5068).
  const { data: globalOverviewFile } = useGetFile(
    {
      global: true,
      name: `definitions/${risk?.name}`,
    },
    { enabled: !!risk, retry: 0, staleTime: Infinity }
  );

  const globalOverviewValue = getFileString(globalOverviewFile);

  // Verification-tab fallback (ENG-5067, extended ENG-5068): manual (human-created)
  // findings never write the evidence/{riskKey} file the POE Verification "Analysis"
  // pane reads, so it shows "No verification notes" even though the write-up exists in
  // the definition files the Overview Description renders. Mirror Overview's full
  // Instance > Account > Global precedence (renderOverviewContent) and strip the same
  // metadata so the fallback prose matches Overview exactly. A finding whose write-up
  // lives ONLY at the Global tier (the shared canonical Praetorian definition) would
  // otherwise render prose in Overview but an empty Verification pane — ENG-5068 closes
  // that gap. `risk.comment` stays excluded (a short annotation, not the Description).
  // '' when no tier holds content — POE then keeps its own empty-state card.
  const verificationFallbackValue = resolveVerificationFallback(
    instanceOverviewValue,
    definitionsFileValue || '',
    globalOverviewValue
  );

  // Evidence file hooks for auxiliary information. surfaceErrors: a genuine
  // load failure must land in `isError` (instead of masquerading as an empty
  // file) so edit mode can be gated below — otherwise the editor seeds a ''
  // draft over real server content and Save destroys it. An S3 404 ("no
  // evidence file yet") still resolves to '' — see useGetFile.
  const {
    data: evidenceFile,
    isError: evidenceLoadFailed,
    isSuccess: evidenceLoaded,
    isFetching: isEvidenceFetching,
  } = useGetFile(
    {
      name: `evidence/${risk?.key}`,
    },
    {
      enabled: !!risk,
      surfaceErrors: true,
    }
  );

  // F1 — file freshness: when the risk's update marker changes while the
  // drawer is open (e.g. a cato run publishes new verification evidence and/or
  // a corrected_description, and a risk refetch lands a newer `updated`),
  // invalidate the per-risk file queries this drawer reads so they refetch:
  //   - evidence/{key}    → editor seed AND the POE Verification pane
  //                         (usePOEData shares the same query key)
  //   - definitions/{key} → the Overview tab's instance-tier description
  //                         (instanceOverviewValue). corrected_description
  //                         overwrites this file server-side; without this the
  //                         active query stays fresh (5-min staleTime, retry:0,
  //                         a prior 404 cached as '') and the Overview keeps
  //                         showing the pre-correction account/global text.
  // Ref-compare so mount/first-load never fires; the key is tracked too so
  // switching risks resets the baseline instead of firing a spurious
  // invalidation.
  const prevEvidenceMarker = useRef<{ key?: string; updated?: string }>({
    key: undefined,
    updated: undefined,
  });
  useEffect(() => {
    const prev = prevEvidenceMarker.current;
    prevEvidenceMarker.current = { key: risk?.key, updated: risk?.updated };
    if (!risk?.key || !risk?.updated) return;
    if (
      prev.key === risk.key &&
      prev.updated &&
      prev.updated !== risk.updated
    ) {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: `evidence/${risk.key}` }),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: `definitions/${risk.key}` }),
      });
    }
  }, [risk?.key, risk?.updated]);

  const { mutateAsync: updateFile, status: updateFileStatus } = useUploadFile();

  const evidenceFileValue = getFileString(evidenceFile);

  // Seed the editor draft from the current evidence value on the closed→open
  // transition, then keep a PRISTINE draft backfilled while the editor is
  // open. `wasEditingAuxiliaryInfo` tracks the previous `isEditingAuxiliaryInfo`
  // (transition detection); `lastSeededAuxInfo` records the value the draft
  // was last seeded from. Invariant: while the editor is open, a change to
  // `evidenceFileValue` (e.g. the evidence fetch settling AFTER the editor
  // opened — useGetFile returns '' during load and coerces errors to '' — or
  // a refetch/invalidation landing) re-seeds the draft ONLY while it is
  // pristine (draft === last seeded value). Once the user has typed, the draft
  // differs from the last seeded value and is NEVER reseeded — the mid-edit
  // no-clobber guarantee is preserved. Without the backfill, an open-before-
  // fetch-settled editor seeded '' forever and Save (unconditional) silently
  // replaced the server content. Behavior on close (true→false) is unchanged —
  // the draft is left as-is.
  const wasEditingAuxiliaryInfo = React.useRef(false);
  const lastSeededAuxInfo = React.useRef('');
  React.useEffect(() => {
    if (isEditingAuxiliaryInfo) {
      const isOpening = !wasEditingAuxiliaryInfo.current;
      const isPristine = auxiliaryInfoValue === lastSeededAuxInfo.current;
      if (
        (isOpening || isPristine) &&
        auxiliaryInfoValue !== evidenceFileValue
      ) {
        setAuxiliaryInfoValue(evidenceFileValue);
        lastSeededAuxInfo.current = evidenceFileValue;
      } else if (isOpening) {
        // Seed value unchanged in state but still record it, so pristineness
        // is measured against THIS session's seed.
        lastSeededAuxInfo.current = evidenceFileValue;
      }
    }
    wasEditingAuxiliaryInfo.current = isEditingAuxiliaryInfo;
  }, [isEditingAuxiliaryInfo, evidenceFileValue, auxiliaryInfoValue]);

  // Fixed: Add null check before calling getMLPredictionFromHistory
  // This prevents the crash when opening vulnerability from share link
  const mlPrediction = useMemo(() => {
    if (!risk) return undefined;
    return getMLPredictionFromHistory(risk);
  }, [risk]);

  const selectedTab =
    computedTabOptions.find(tab => tab.value === currentTab) ||
    computedTabOptions[0];

  // Arm POE mode whenever the selected tab is Evidence — covers BOTH
  // mount-direct-on-Evidence (the p-open path) AND user tab clicks. The tab
  // `onSelect` handler also arms synchronously so a click registers even before
  // the local tab state settles; this effect is the load-bearing writer for the
  // no-click mount case (T6). The two writes set the same boolean and are
  // idempotent, so the overlap is intentional and harmless.
  React.useEffect(() => {
    poeMode.setEnabled(selectedTab.value === 'evidence');
    // Dep list is intentionally just [selectedTab.value]: poeMode.setEnabled is
    // a stable store setter, so re-arming only needs to track the selected tab.
  }, [selectedTab.value]);

  const { mutateAsync: updateRisk } = useUpdateRisk();
  const { mutate: refreshVisited, status: refreshVisitedStatus } =
    useRefreshVisited();

  async function handleUpdateRisk(risk: Risk) {
    await updateRisk(risk);
  }

  const handleHackerOneReply = useCallback(
    async (
      reportId: string,
      source: 'hacker-api' | 'org-api',
      message: string,
      internal: boolean,
      attachmentS3Keys?: string[]
    ) => {
      await axios.post('/hackerone/comment', {
        report_id: reportId,
        message,
        internal,
        source,
        ...(attachmentS3Keys && attachmentS3Keys.length > 0
          ? { attachment_s3_keys: attachmentS3Keys }
          : {}),
      });

      // Invalidate the activities query so the new comment appears immediately.
      await queryClient.invalidateQueries({
        queryKey: ['hackerone', 'activities', reportId, source],
      });
    },
    [axios]
  );

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);

  // ✅ FIXED: Move all hooks BEFORE the early return to comply with Rules of Hooks
  // All hooks must be called unconditionally before any early returns

  const assetParentFilters = useMemo(
    () => ({ riskKey: risk?.key || '' }),
    [risk?.key]
  );

  const {
    data: impactedAssets,
    status: impactedAssetsStatus,
    isPlaceholderData: impactedAssetsArePlaceholder,
    hasNextPage: impactedAssetsHaveMorePages,
  } = useQueryAssets({
    parentFilters: assetParentFilters,
    excludeAccountSources: true,
    enabled: !!risk,
  });

  // Display labels for the "Assets Affected" markdown section (ENG-5383).
  //
  // Dedupe by `asset.key`: useQueryAssets concatenates two independent queries
  // and only dedupes WITHIN each leg, so the same asset can arrive twice.
  //
  // The gate is `status === 'success' && !isPlaceholderData`, NOT `isFetching`.
  // `isPlaceholderData` means precisely "these rows belong to a different query
  // key" — under `keepPreviousData` the observer reports `status: 'success'`
  // while still serving the PREVIOUS finding's assets, and listing those would
  // attribute the wrong assets to this finding. `isFetching`, by contrast, also
  // fires on ordinary background revalidation of THIS finding (staleTime + v5's
  // default refetchOnWindowFocus), so gating on it would blank correct content
  // on every tab return.
  const affectedAssetLabels = useMemo(() => {
    if (impactedAssetsStatus !== 'success' || impactedAssetsArePlaceholder) {
      return [];
    }
    const uniqueAssets = Array.from(
      new Map((impactedAssets ?? []).map(a => [a.key, a])).values()
    );
    return uniqueAssets.map(asset =>
      getDisplayIdentifierWithFallback(asset, asset.key)
    );
  }, [impactedAssets, impactedAssetsStatus, impactedAssetsArePlaceholder]);

  // Whether `affectedAssetLabels` is only a PREFIX of this finding's assets.
  //
  // `useQueryAssets` is paginated and is not auto-paginated here: the main leg
  // requests a bounded page (`limit`, default 100) and the risk-scoped leg takes
  // the backend default page size, and this drawer deliberately never calls the
  // returned `fetchNextPage` — a large roll-up finding would otherwise fire
  // hundreds of sequential requests during a drawer render. So instead of
  // fetching everything we disclose the shortfall in the rendered section and
  // point at the drawer's Assets tab, which paginates properly and holds the
  // complete list.
  //
  // Both legs are risk-scoped in drawer context (the main leg reaches assets via
  // the risk attribute, the second via the direct HAS_VULNERABILITY
  // relationship), so the hook's `hasNextPage` OR is a correct "the combined
  // association set is incomplete" signal.
  //
  // Gated on the same success/non-placeholder condition as the labels, plus a
  // non-empty label list: a truncation note must never render without a list
  // above it.
  const affectedAssetsTruncated =
    impactedAssetsStatus === 'success' &&
    !impactedAssetsArePlaceholder &&
    affectedAssetLabels.length > 0 &&
    Boolean(impactedAssetsHaveMorePages);

  const { data: accessGeneric } = useGenericSearch({
    query: '#access',
  });
  const accesss = accessGeneric?.accesss || [];

  const { data: evidenceFiles } = useEvidenceFiles(risk?.key || '');

  // Server tickets from API (source of truth)
  // Supports any ticketing integration (Jira, Azure DevOps, etc.)
  // Tickets are now embedded on the Risk node itself
  const serverTicketsRaw = risk?.tickets || [];
  const serverTicketsFingerprint = serverTicketsRaw
    .map(t => `${t.provider}:${t.id}:${t.status}`)
    .join(',');
  const serverTickets = useMemo(
    () => serverTicketsRaw,
    // Stabilize: only change when ticket identities actually change
    [serverTicketsFingerprint]
  );

  // Optimistic tickets for instant UI feedback when status changes to "Opened"
  // Shows "Creating..." placeholder immediately, replaced by real ticket on refetch
  const {
    tickets,
    addOptimisticTicket,
    hasOptimisticTicket,
    removeOptimisticTicket,
    restoreOptimisticTicket,
  } = useOptimisticTickets(serverTickets);

  const { accounts: ticketingAccounts } = useTicketingAccounts();

  // Refs for effect body — fresh values without being dependencies
  const serverTicketsRef = useRef(serverTickets);
  const ticketingAccountsRef = useRef(ticketingAccounts);

  useEffect(() => {
    serverTicketsRef.current = serverTickets;
    ticketingAccountsRef.current = ticketingAccounts;
  });

  const [refreshedMetadata, setRefreshedMetadata] = useState<
    Record<string, { status?: string; assignee?: string; resolution?: string }>
  >({});

  // Fetch fresh ticket data when the drawer opens or after a mutation changes the risk
  useEffect(() => {
    const currentTickets = serverTicketsRef.current;
    const currentAccounts = ticketingAccountsRef.current;

    // Skip if no tickets
    if (currentTickets.length === 0) return;

    const controller = new AbortController();

    const fetchTicketData = async () => {
      const newMetadata: Record<
        string,
        { status?: string; assignee?: string; resolution?: string }
      > = {};

      for (const ticket of currentTickets) {
        try {
          const ticketKey = `${ticket.provider}:${ticket.id}`;

          // Find the account whose member matches this ticket's provider.
          const matchingAccount = currentAccounts.find(account =>
            account.member.startsWith(ticket.provider)
          );

          if (!matchingAccount) {
            continue;
          }

          if (risk?.key && ticket.id) {
            const freshData = await fetchTicket(
              axios,
              risk.key,
              ticket.id,
              matchingAccount.key
            );

            const updatedRisk = freshData?.risks?.[0];
            const updatedTicket = updatedRisk?.tickets?.find(
              t => t.id === ticket.id
            );

            newMetadata[ticketKey] = updatedTicket
              ? {
                  status: updatedTicket.status ?? '',
                  assignee: updatedTicket.assignee ?? '',
                  resolution: updatedTicket.resolution ?? '',
                }
              : {
                  status: ticket.status,
                  assignee: ticket.assignee,
                  resolution: ticket.resolution,
                };
          }
        } catch (error) {
          console.error('Failed to fetch ticket data:', error);
        }
      }

      if (!controller.signal.aborted) {
        setRefreshedMetadata(newMetadata);
      }
    };

    fetchTicketData();

    return () => {
      controller.abort();
    };
    // Re-fetch when:
    // - vulnerability changes (risk?.key)
    // - risk status is updated post-mutation (risk?.status)
    // - embedded ticket data changes after async Jira sync (serverTicketsFingerprint)
    // serverTickets/ticketingAccounts accessed via refs to avoid unstable array deps.
  }, [risk?.key, risk?.status, serverTicketsFingerprint, axios]);

  const historyString = useMemo(
    () => JSON.stringify(risk?.history),
    [risk?.history]
  );

  const history = useMemo(() => {
    if (!risk) return [];

    const riskHistory = risk.history || [];

    const originalStatus = riskHistory[0]?.from || risk.status;

    const firstTrackedHistory = {
      from: '',
      to: originalStatus,
      updated: risk.created,
      comment: risk.comment,
    };

    return [firstTrackedHistory, ...riskHistory];
  }, [historyString, risk?.status, risk?.created, risk?.comment]);

  const userDefinedTags = useMemo(() => {
    // Hide internal hunt/agent provenance tags from customers (Praetorian sees all)
    return filterInternalTags(risk?.tags, maskingUserType);
  }, [risk?.tags, maskingUserType]);

  const { ssoTags, isSSO } = useMemo(() => {
    const sso = (risk?.name || '').startsWith('web-login-sso');
    return {
      ssoTags: sso ? ['web-login-sso'] : [],
      isSSO: sso,
    };
  }, [risk?.name]);

  const { moreActions: baseMoreActions } = useRiskActions({
    risks: risk ? [risk] : [],
    onSuccess: () => refetch(),
    onComment: () => setIsNoteModalOpen(true),
    showTicketActions: true,
    onCreateTicket: () => {
      setIsTicketModalOpen(true);
    },
  });

  // ✅ All hooks called - now safe to do early return

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!risk) return null;

  // Handler functions defined after hooks but before render
  const handleNote = async (note: string): Promise<void> => {
    await updateRisk({
      key: risk.key,
      name: risk.name,
      status: risk.status,
      comment: note,
      showSnackbar: false,
    });
    toast.success('Note added to risk');
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    try {
      const updatedTags = (risk?.tags || []).filter(tag => tag !== tagToRemove);
      await axios.put('/risk', {
        key: risk.key,
        tags: updatedTags,
      });
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            (queryKey[0] === 'MY' && queryKey[1] === 'risk') ||
            queryKey[0] === 'GENERIC_MY_SEARCH'
          );
        },
        refetchType: 'active',
      });
      toast.success('Tag removed');
    } catch {
      toast.error('Failed to remove tag');
    }
  };

  // Filter baseMoreActions to only include ticket-related actions
  const ticketActions = baseMoreActions.filter(action =>
    ['new-ticket', 'associate-ticket', 'setup-ticketing'].includes(
      action.value as string
    )
  );

  // PlexTrac actions dropdown - only for Praetorian users
  const plexTracActions = isPraetorianUser
    ? [
        {
          label: 'Send to PlexTrac',
          icon: <ArrowTopRightOnSquareIcon className="size-5" />,
          onClick: () => setIsPlexTracModalOpen(true),
        },
        {
          label: 'Sync With PlexTrac',
          icon: <ArrowPathIcon className="size-5" />,
          onClick: () =>
            syncWithPlextrac(
              { id: risk.name, create: false },
              {
                onSuccess: () => {
                  refetchDefinitionsFile();
                  queryClient.invalidateQueries({
                    queryKey: getQueryKey.getMy('vulnerability'),
                  });
                },
                onError: () => {
                  refetchDefinitionsFile();
                  queryClient.invalidateQueries({
                    queryKey: getQueryKey.getMy('vulnerability'),
                  });
                },
              }
            ),
        },
      ]
    : [];

  // Ticketing actions - only ticket-related items
  const moreActions = ticketActions;

  // Attack-surface edit gate (DRAWER-ONLY): Praetorian users may reclassify a
  // finding's surface when it was Praetorian-created OR has no surface yet.
  // Options are derived to {label,value}[] and passed to VulnerabilityActions
  // ONLY when the gate is open, so the "Change Surface" submenu never
  // appears on the main vulnerabilities table (which omits this prop).
  const showAttackSurfaceEdit =
    isPraetorianUser &&
    (isPraetorianCreatedRisk(risk) || !risk.attackSurface?.length);
  const attackSurfaceEditOptions = showAttackSurfaceEdit
    ? getAttackSurfaceChangeOptions()
        .filter(option => option.value)
        .map(option => ({ label: option.label, value: option.value as string }))
    : undefined;

  return (
    <div className="flex w-full flex-col gap-6 p-6">
      <VulnerabilityDrawerHeader
        risk={risk}
        existingWriteup={existingWriteup}
        vulnerabilityTitle={vulnerabilityTitle}
        mlPrediction={mlPrediction}
        isSSO={isSSO}
        ssoTags={ssoTags}
      />
      <SimpleTags
        tags={userDefinedTags}
        onRemove={canWriteAssets ? handleRemoveTag : undefined}
        variant="compact"
      />

      <Entitled entitlement="write_assets">
        <div className="flex flex-wrap items-center gap-2">
          <VulnerabilityActions
            vulnerabilities={[risk]}
            onNoteClick={() => setIsNoteModalOpen(true)}
            onTagClick={() => setIsTagModalOpen(true)}
            onRemoveTagClick={() => setIsRemoveTagModalOpen(true)}
            onImportClick={() => setIsImportModalOpen(true)}
            onExportClick={() => setIsExportModalOpen(true)}
            onAuxInfoClick={() => {
              // F2: a failed evidence load means the current server content is
              // unknown — opening the editor would seed a draft from the
              // coerced/absent value and Save (unconditional) would overwrite
              // whatever is actually stored. Block entry until a retry works.
              if (evidenceLoadFailed) {
                toast.error(
                  "Verification notes couldn't be loaded. Retry or refresh the page before editing."
                );
                return;
              }
              // F3: block edit-entry unless the evidence query has SETTLED
              // successfully AND is not currently (re)fetching. Two windows to
              // close, both seeding the editor from a value the server may be
              // about to replace:
              //  - initial load in flight: isSuccess false, isError false →
              //    data undefined → evidenceFileValue ''. Opening seeds a ''
              //    draft.
              //  - background refetch after the freshness effect above
              //    invalidates evidence/{key} when risk.updated advances:
              //    TanStack keeps status 'success' (evidenceLoaded stays true)
              //    and RETAINS the stale data while refetching (isFetching
              //    true). Opening now seeds the STALE value; a Save before the
              //    refetch lands silently overwrites the newer server content
              //    the user never saw.
              // In both windows, once the user types the draft is non-pristine
              // and the pristine-backfill will NOT re-seed the settled server
              // content, so gate entry on the query being settled AND idle.
              if (!evidenceLoaded || isEvidenceFetching) {
                toast.error(
                  'Verification notes are still loading. Try again in a moment.'
                );
                return;
              }
              setIsEditingAuxiliaryInfo(true);
            }}
            onRefreshVisited={(comment: string) =>
              refreshVisited({ risk, comment })
            }
            isRefreshingVisited={refreshVisitedStatus === 'pending'}
            plexTracActions={plexTracActions}
            moreActions={moreActions}
            onConstantineJobStarted={() => setCurrentTab('evidence')}
            triggerPatchAndPR={triggerPatchAndPR}
            isPatchAndPRLoading={isPatchAndPRLoading}
            onUpdateRisk={async updatedRisk => {
              // Check if status is changing to "Opened" - this triggers Jira ticket creation
              const isChangingToOpened =
                updatedRisk.status.startsWith(RiskStatus.Opened) &&
                !risk.status.startsWith(RiskStatus.Opened);

              // Show optimistic placeholder immediately for instant feedback
              // Only show if: 1) changing to opened, 2) no optimistic placeholder, 3) no existing tickets
              if (
                isChangingToOpened &&
                !hasOptimisticTicket &&
                serverTickets.length === 0
              ) {
                addOptimisticTicket();
              }

              await handleUpdateRisk(updatedRisk);
            }}
            severityOptions={severityFilters}
            attackSurfaceOptions={attackSurfaceEditOptions}
            getRiskStatusLabel={getRiskStatusLabel}
            hideLabel
          />
        </div>
      </Entitled>
      <div className="bg-chariot-stroke h-px w-full"></div>
      <TabsKeyboard
        id="vulnerabilityDetailsTabs"
        tabs={computedTabOptions}
        keyboardDependencies={[risk.name]}
        selected={selectedTab}
        onSelect={tabOption => {
          // Dual-write with the mount/tab effect, intentionally. This arms POE
          // synchronously on the click so it registers before local tab state
          // settles; the effect (dep [selectedTab.value]) then re-runs and sets
          // the SAME boolean — idempotent, so the second write is a no-op. The
          // effect remains the load-bearing writer for the no-click mount case
          // (T6, drawer opened directly on Evidence). Keep both.
          poeMode.setEnabled(tabOption.value === 'evidence');
          setCurrentTab(tabOption.value);
        }}
      />

      {/* Unified tab content structure with conditional overflow for Evidence tab */}
      <div className="w-full">
        <Container
          className={cn('p-6', selectedTab.value === 'evidence' && 'pl-2')}
          tabIndex={-1}
        >
          {selectedTab.value === 'overview' && (
            <OverviewTab
              risk={risk}
              vulnerabilityId={vulnerabilityId}
              tickets={tickets}
              refetchTickets={async () => {
                // Tickets are embedded on Risk, so refetching means invalidating risk queries
                await queryClient.invalidateQueries({
                  queryKey: getQueryKey.getMy('risk'),
                });
              }}
              removeOptimisticTicket={removeOptimisticTicket}
              restoreOptimisticTicket={restoreOptimisticTicket}
              refreshedMetadata={refreshedMetadata}
              instanceOverviewValue={instanceOverviewValue}
              definitionsFileValue={definitionsFileValue}
              isDefinitionsFileFetching={isDefinitionsFileFetching}
              _refetchDefinitionsFile={refetchDefinitionsFile}
              hasExistingWriteup={hasExistingWriteup}
              _syncWithPlextrac={syncWithPlextrac}
              impactedAssets={impactedAssets}
              affectedAssetLabels={affectedAssetLabels}
              affectedAssetsTruncated={affectedAssetsTruncated}
            />
          )}
          {selectedTab.value === 'assets' && risk?.key && (
            <AssetsTab riskKey={risk.key} />
          )}
          {selectedTab.value === 'evidence' && (
            <div data-testid="evidence-tab">
              <POESelector
                evidenceFiles={evidenceFiles || []}
                key={risk.key}
                poeLocation={`${risk.dns}/${risk.name}`}
                name={risk.name}
                source={risk.source}
                riskKey={risk.key}
                severity={risk.status[1] as RiskSeverity}
                attributes={attributesGenericSearch?.attributes}
                verificationFallbackValue={verificationFallbackValue}
                patches={risk.patches}
                onExitPoeMode={() => handleOpenVulnerabilityDrawer()}
              />
              <CorrelationGraph risk={risk} />
            </div>
          )}
          {selectedTab.value === 'history' && (
            <Timeline
              items={[
                ...(history
                  ?.map((item, itemIndex) => {
                    const { title, updated } = getHistoryDiff(
                      item,
                      itemIndex === 0,
                      accesss,
                      maskingUserType
                    );
                    return {
                      title,
                      description: updated,
                      className: 'text-default',
                      icon:
                        itemIndex === 0 ? (
                          <RisksIcon className="stroke-1" />
                        ) : undefined,
                    };
                  })
                  .reverse() || []),
              ]}
            />
          )}
          {selectedTab.value === 'notes' && (
            <NotesTab
              resource={risk}
              resourceType="Vulnerability"
              onOpenCommentModal={
                canWriteAssets ? () => setIsNoteModalOpen(true) : undefined
              }
            />
          )}
          {selectedTab.value === 'hackerone' && risk && (
            <HackerOneReportPanel
              attributes={attributesGenericSearch?.attributes ?? []}
              onReply={handleHackerOneReply}
            />
          )}
        </Container>
      </div>
      <NoteModal
        open={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSubmit={handleNote}
        resourceType="Risk"
        mode="markdown"
        presentation="modal"
        selectedCount={1}
      />

      <AddTag
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        description="Tags help organize and classify vulnerabilities in your attack surface"
        disabled={false}
        entity="risk"
        onSubmit={async (newTag: string) => {
          try {
            const existingTags = risk?.tags || [];
            await axios.put('/risk', {
              key: risk.key,
              tags: [...existingTags, newTag],
            });
            queryClient.invalidateQueries({
              predicate: query => {
                const queryKey = query.queryKey as string[];
                return (
                  (queryKey[0] === 'MY' && queryKey[1] === 'risk') ||
                  queryKey[0] === 'GENERIC_MY_SEARCH'
                );
              },
              refetchType: 'active',
            });
            toast.success('Tag added');
          } catch {
            toast.error('Failed to add tag');
          }
        }}
      />

      {/* Single-item context (riskKeys=[risk.key]) — shouldUseBulk is always false; no bulk props needed. */}
      <VulnerabilityRemoveTag
        open={isRemoveTagModalOpen}
        onClose={() => setIsRemoveTagModalOpen(false)}
        riskKeys={[risk.key]}
        onSuccess={() => {
          refetch();
          setIsRemoveTagModalOpen(false);
        }}
      />

      {isTicketModalOpen && (
        <CreateTicketModal
          risk={risk}
          onClose={() => setIsTicketModalOpen(false)}
        />
      )}

      <PushToPlexTracModal
        isOpen={isPlexTracModalOpen}
        onClose={() => setIsPlexTracModalOpen(false)}
        riskKey={risk.key}
        riskName={risk.name}
      />

      <Modal
        size="6xl"
        open={isEditingAuxiliaryInfo}
        onClose={() => setIsEditingAuxiliaryInfo(false)}
        title="Verification"
        footer={{
          text: 'Save',
          isLoading: updateFileStatus === 'pending',
          onClick: async () => {
            await updateFile({
              ignoreSnackbar: true,
              name: `evidence/${risk.key}`,
              content: auxiliaryInfoValue,
            });
            queryClient.invalidateQueries();
            setIsEditingAuxiliaryInfo(false);
          },
        }}
      >
        <div className="h-[60vh]">
          <MarkdownEditor
            value={auxiliaryInfoValue}
            onChange={value => setAuxiliaryInfoValue(value || '')}
            filePathPrefix="definitions/files"
          />
        </div>
      </Modal>

      {/* Import Modal - lazy loaded to avoid heavy dependencies */}
      {isImportModalOpen && (
        <Suspense fallback={null}>
          <ImportVulnerabilitiesModal
            open={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
          />
        </Suspense>
      )}

      {/* Export Modal */}
      <UniversalExportModal
        config={exportConfig}
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        selectedRows={[risk.key]}
        onClearSelection={() => setIsExportModalOpen(false)}
      />
    </div>
  );
};

const SSVC_DECISION_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  Track: {
    bg: 'bg-chariot-status-info/15',
    text: 'text-chariot-status-info',
    label: 'Track',
  },
  'Track*': {
    bg: 'bg-chariot-status-medium/15',
    text: 'text-chariot-status-medium',
    label: 'Track*',
  },
  Attend: {
    bg: 'bg-chariot-status-high/15',
    text: 'text-chariot-status-high',
    label: 'Attend',
  },
  Act: {
    bg: 'bg-chariot-status-critical/15',
    text: 'text-chariot-status-critical',
    label: 'Act',
  },
};

const SsvcDecisionBadge = ({ decision }: { decision: string }) => {
  const style = SSVC_DECISION_STYLES[decision] ?? {
    bg: 'bg-chariot-background-secondary',
    text: 'text-chariot-text-primary',
    label: decision,
  };
  return (
    <span
      className={`${style.bg} ${style.text} inline-flex h-9 items-center rounded-md px-3 text-sm font-semibold`}
      data-testid="ssvc-decision-badge"
    >
      {style.label}
    </span>
  );
};

const overviewTabs = [
  { label: 'Description', value: '' },
  { label: 'Risk Score', value: 'risk-score' },
  { label: 'Exploitation Activities', value: 'exploitation-activities' },
  { label: 'MITRE', value: 'mitre' },
  { label: 'SSVC', value: 'ssvc' },
  { label: 'Frameworks', value: 'frameworks' },
];

const OverviewTab = ({
  risk,
  vulnerabilityId,
  tickets,
  refetchTickets,
  removeOptimisticTicket,
  restoreOptimisticTicket,
  refreshedMetadata,
  instanceOverviewValue,
  definitionsFileValue,
  isDefinitionsFileFetching,
  _refetchDefinitionsFile,
  hasExistingWriteup,
  _syncWithPlextrac,
  impactedAssets,
  affectedAssetLabels,
  affectedAssetsTruncated,
}: {
  risk: Risk;
  vulnerabilityId?: string;
  tickets: EmbeddedTicket[];
  refetchTickets: () => Promise<void>;
  removeOptimisticTicket: (ticketKey: string) => void;
  restoreOptimisticTicket: () => void;
  refreshedMetadata: Record<
    string,
    { status?: string; assignee?: string; resolution?: string }
  >;
  instanceOverviewValue: string;
  definitionsFileValue: string;
  isDefinitionsFileFetching: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _refetchDefinitionsFile: () => Promise<any>;
  hasExistingWriteup: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _syncWithPlextrac: (definition: PlextracDefinition, options?: any) => void;
  impactedAssets?: Asset[];
  /**
   * Escaped-at-render display labels for the "Assets Affected" section
   * (ENG-5383). Already deduped by asset key and already gated on the assets
   * query having settled on THIS finding's key — an empty array means "do not
   * splice", never "still loading".
   */
  affectedAssetLabels: string[];
  /**
   * `true` when `affectedAssetLabels` is only the first page of this finding's
   * assets (ENG-5383). The assets query is paginated with a bounded page size
   * and the drawer does not walk it to exhaustion, so the section discloses the
   * shortfall and points at the Assets tab for the complete list. Already gated
   * on the same success/non-placeholder condition as `affectedAssetLabels` and
   * on that array being non-empty — it can never be `true` without a list.
   */
  affectedAssetsTruncated: boolean;
}) => {
  // Check for CVE in risk.name OR attached vulnerability's id
  const cveId = risk.name.startsWith('CVE-')
    ? risk.name
    : vulnerabilityId?.startsWith('CVE-')
      ? vulnerabilityId
      : undefined;
  const isCveRisk = Boolean(cveId);
  const { isPraetorianUser } = useAuth();
  const {
    data: globalFile,
    isFetching: isGlobalFileFetching,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch: _refetchGlobalFile,
  } = useGetFile(
    {
      global: true,
      name: `definitions/${risk?.name}`,
    },
    {
      enabled: !!risk,
      retry: 0,
      staleTime: Infinity,
    }
  );

  const relatedTicketsRef = useRef<HTMLDivElement>(null);
  const markdownPreviewRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTabValue = searchParams.get('risk-drawer-overview-tab');

  const { data: threatIntel, status: cveFileStatus } =
    useGetFile<EnrichedVulnerability>(
      { name: `enrichments/cve/${cveId}.json`, global: true },
      { enabled: isCveRisk }
    );

  const { data: riskEnrichmentRaw } = useGetFile<RiskFrameworkEnrichment>(
    { name: risk.name ? riskFrameworkS3Key(risk.name) : '' },
    { enabled: !!risk.name }
  );

  const frameworkRefs = useMemo(() => {
    const enrichment = parseRiskFrameworkEnrichment(riskEnrichmentRaw);
    if (!enrichment) return [];
    return riskEnrichmentToFrameworkRefs(enrichment);
  }, [riskEnrichmentRaw]);

  // Check if instance, account, and global definitions have actual content after
  // stripping definition metadata (ENG-5075): a metadata-only higher tier (Risk ID
  // + Title headers, no body) strips to empty, so it no longer masks real
  // lower-tier prose — mirrors resolveVerificationFallback's strip-then-select.
  const hasValidInstanceContent =
    stripDefinitionMetadata(instanceOverviewValue ?? '').trim().length > 0;

  const hasValidAccountContent =
    stripDefinitionMetadata(definitionsFileValue ?? '').trim().length > 0;

  const globalFileContent = getFileString(globalFile);
  const hasValidGlobalContent =
    stripDefinitionMetadata(globalFileContent).trim().length > 0;

  const selectedTab =
    (selectedTabValue
      ? overviewTabs.find(tab => tab.value === selectedTabValue)
      : undefined) || overviewTabs[0];

  const { status: reportRiskStatus } = useReportRisk();
  const { mutateAsync: updatePublicWellBeing, status: wbStatus } =
    useUpdateRiskPublicWellBeing();

  const handlePublicWellBeingChange = async (value: string) => {
    if (value === risk.public_well_being) return;
    try {
      await updatePublicWellBeing({
        key: risk.key,
        public_well_being: value as 'minimal' | 'material' | 'irreversible',
      });
      toast.success('Public well-being updated');
    } catch {
      toast.error('Failed to update public well-being');
    }
  };

  useEffect(() => {
    function calculatePreviewWidth() {
      if (!markdownPreviewRef.current) return;

      const relatedTicketHeight = relatedTicketsRef?.current?.clientHeight || 0;

      const markdownContainer =
        markdownPreviewRef.current.querySelector('.wmde-markdown');
      if (!markdownContainer) return;

      const containerTop = markdownContainer.getBoundingClientRect().top;

      // Reset all children to original width
      const children = Array.from(markdownContainer.children) as HTMLElement[];
      children.forEach(child => {
        const childTop = child.getBoundingClientRect().top - containerTop;

        if (childTop <= relatedTicketHeight) {
          child.style.width = `calc(100% - ${relatedTicketsWidth}px)`;
        } else {
          child.style.width = '100%';
        }
      });
    }

    const relatedTicketHeightObserver = new ResizeObserver(
      calculatePreviewWidth
    );
    const markdownContainerObserver = new ResizeObserver(calculatePreviewWidth);

    if (relatedTicketsRef.current) {
      relatedTicketHeightObserver.observe(relatedTicketsRef.current);
    }

    if (markdownPreviewRef.current) {
      markdownContainerObserver.observe(markdownPreviewRef.current);
    }

    // Initial dimensions
    calculatePreviewWidth();

    return () => {
      relatedTicketHeightObserver.disconnect();
      markdownContainerObserver.disconnect();
    };
  }, [definitionsFileValue, globalFileContent, tickets.length]);

  const hasEnrichmentTabs =
    (isCveRisk && !!threatIntel) || frameworkRefs.length > 0;

  const visibleOverviewTabs = useMemo(() => {
    if (!hasEnrichmentTabs) return [overviewTabs[0]];
    return overviewTabs.filter(tab => {
      if (tab.value === '' || tab.value === 'ssvc') return true;
      if (tab.value === 'frameworks')
        return (isCveRisk && !!threatIntel) || frameworkRefs.length > 0;
      return isCveRisk && !!threatIntel;
    });
  }, [hasEnrichmentTabs, isCveRisk, threatIntel, frameworkRefs.length]);

  const isOverview = selectedTab.value === '';

  const renderOverviewContent = useCallback(() => {
    // Priority: Instance > Account > Global > None
    let source = hasValidInstanceContent
      ? instanceOverviewValue
      : hasValidAccountContent
        ? definitionsFileValue
        : hasValidGlobalContent
          ? globalFileContent
          : null;

    // Strip metadata sections that should not be rendered in the UI.
    // PlexTrac path: GitHub markdown → PlexTrac parses and strips Title/Risk ID → stores as Writeup.title → Chariot syncs → S3 stores only 4 content sections.
    // AI path: AI generates markdown with Title/Risk ID → writes directly to S3 → UI renders raw markdown including metadata sections.
    // The backend extraction regex (risk.go) only recognizes 4 sections: Vulnerability Description, Impact, Recommendation, References.
    // Title comes from Risk.Name (Neo4j) and Risk ID is not displayed separately.
    // Without this stripping, these sections appear as raw markdown in the card content.
    if (source) {
      source = stripDefinitionMetadata(source);
    }

    // Splice an "Assets Affected" section into the description between Impact
    // and Recommendation (ENG-5383). The asset can't live in the definition
    // templates — `definitions/<Risk.Name>` is shared by every finding of that
    // type across every tenant — so it is injected here, once, for every
    // template shape. When there is no description source at all we fall
    // through to the "No Information Collected" empty state rather than
    // synthesizing a body just to host the section.
    //
    // `affectedAssetsTruncated` discloses when the list is only a first page:
    // the assets query has a bounded page size and the drawer does not fetch
    // every page (a large roll-up finding would mean hundreds of sequential
    // requests mid-render), so the section says so out loud and sends the
    // reader to the drawer's Assets tab, which paginates and holds the whole
    // list.
    if (source) {
      source = injectAffectedAssetsSection(source, affectedAssetLabels, {
        truncated: affectedAssetsTruncated,
      });
    }

    if (source) {
      return (
        <MarkdownPreview
          source={source}
          className={cn(tickets.length === 0 && `*:w-full`, 'break-words')}
          style={{
            padding: '0.75rem',
            minHeight: '20px',
          }}
          processVulnerabilityContent={true}
        />
      );
    } else {
      return (
        <div className="text-chariot-text-secondary flex h-full flex-col items-center justify-center py-4 text-center">
          <NoInformationIcon className="text-chariot-stroke mb-4 min-h-[200px]" />
          <h2 className="text-chariot-text-primary text-lg font-bold">
            No Information Collected
          </h2>
        </div>
      );
    }
  }, [
    hasValidInstanceContent,
    instanceOverviewValue,
    hasValidAccountContent,
    definitionsFileValue,
    hasValidGlobalContent,
    globalFileContent,
    tickets.length,
    affectedAssetLabels,
    affectedAssetsTruncated,
  ]);

  const renderEmptyPlexTracDescription = useCallback(() => {
    if (hasExistingWriteup && !globalFileContent && isPraetorianUser) {
      return (
        <div className="border-chariot-stroke-medium bg-chariot-status-medium/10 mx-3 mb-3 flex items-start gap-3 rounded-md border p-4">
          <ExclamationTriangleIcon className="text-chariot-status-medium mt-0.5 size-5 shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-chariot-text-primary text-sm font-medium">
              Empty PlexTrac Description
            </p>
            <p className="text-chariot-text-secondary text-sm">
              Please add appropriate content to the definition file immediately.
            </p>
          </div>
        </div>
      );
    }

    return null;
  }, [hasExistingWriteup, globalFileContent, isPraetorianUser]);

  const isOverviewLoading =
    (isCveRisk && cveFileStatus === 'pending') ||
    isDefinitionsFileFetching ||
    isGlobalFileFetching ||
    reportRiskStatus === 'pending';

  if (isOverviewLoading) {
    return <Skeleton />;
  }

  return (
    <>
      <div data-testid="overview-tab">
        {hasEnrichmentTabs && visibleOverviewTabs.length > 1 && (
          <Tabs
            className="mb-6"
            tabs={visibleOverviewTabs}
            selected={selectedTab}
            variant="tertiary"
            onSelect={tab => {
              setSearchParams(prev => {
                if (tab.value) {
                  prev.set('risk-drawer-overview-tab', tab.value);
                } else {
                  prev.delete('risk-drawer-overview-tab');
                }
                return prev;
              });
            }}
          />
        )}
        <div className="flex flex-col gap-6">
          {isOverview && (
            <div className="relative">
              <RelatedTickets
                risk={risk}
                isDefinitionsFile={Boolean(definitionsFileValue)}
                tickets={tickets}
                refetchTickets={refetchTickets}
                removeOptimisticTicket={removeOptimisticTicket}
                restoreOptimisticTicket={restoreOptimisticTicket}
                refreshedMetadata={refreshedMetadata}
                ref={relatedTicketsRef}
                className="float-right ml-6"
              />
              <div className="w-full">
                <div ref={markdownPreviewRef}>
                  {renderEmptyPlexTracDescription()}
                  {renderOverviewContent()}
                </div>
              </div>
            </div>
          )}
          {!isOverview && (
            <>
              {selectedTab.value === 'risk-score' && (
                <>
                  <CVSSComponent threatIntel={threatIntel} />
                  <EPSSComponent threatIntel={threatIntel} />
                </>
              )}
              {selectedTab.value === 'exploitation-activities' && (
                <>
                  <ExploitsTimelineComponent threatIntel={threatIntel} />
                  <ThreatActorsComponent threatIntel={threatIntel} />
                </>
              )}
              {selectedTab.value === 'mitre' && (
                <>
                  <FrameworkRefsComponent
                    frameworkRefs={(
                      threatIntel?.framework_refs ?? frameworkRefs
                    ).filter(
                      r =>
                        r.framework === 'mitre_attack' ||
                        r.framework === 'mitre_atlas' ||
                        r.framework === 'd3fend'
                    )}
                  />
                  {threatIntel && (
                    <MitreAttacksComponent threatIntel={threatIntel} />
                  )}
                </>
              )}
              {selectedTab.value === 'frameworks' && (
                <>
                  <FrameworkRefsComponent
                    frameworkRefs={threatIntel?.framework_refs ?? frameworkRefs}
                  />
                  {threatIntel && (
                    <ComplianceFrameworksPanel
                      risk={risk}
                      threatIntel={threatIntel}
                    />
                  )}
                </>
              )}
              {selectedTab.value === 'ssvc' && (
                <>
                  <div className="mb-4 flex flex-wrap items-end gap-6">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-chariot-text-secondary text-xs font-medium">
                        SSVC Public Well-Being
                      </label>
                      <Select
                        value={risk.public_well_being || ''}
                        onValueChange={handlePublicWellBeingChange}
                        disabled={wbStatus === 'pending'}
                      >
                        <SelectTrigger
                          className="w-48"
                          data-testid="public-well-being-select"
                        >
                          <SelectValue placeholder="Not set" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minimal">
                            <span className="font-medium">Minimal</span>
                            <span className="text-chariot-text-secondary ml-2 text-xs">
                              Little to no impact on public well-being
                            </span>
                          </SelectItem>
                          <SelectItem value="material">
                            <span className="font-medium">Material</span>
                            <span className="text-chariot-text-secondary ml-2 text-xs">
                              Material harm that is not irreversible
                            </span>
                          </SelectItem>
                          <SelectItem value="irreversible">
                            <span className="font-medium">Irreversible</span>
                            <span className="text-chariot-text-secondary ml-2 text-xs">
                              Serious or irreversible harm to public well-being
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {risk.ssvc_decision && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-chariot-text-secondary text-xs font-medium">
                          SSVC Decision
                        </label>
                        <SsvcDecisionBadge decision={risk.ssvc_decision} />
                      </div>
                    )}
                  </div>
                  <SsvcDecisionTree
                    risk={risk}
                    threatIntel={threatIntel}
                    impactedAssets={impactedAssets}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const RelatedTickets = forwardRef<
  HTMLDivElement,
  {
    risk: Risk;
    isDefinitionsFile: boolean;
    tickets: EmbeddedTicket[];
    refetchTickets: () => Promise<void>;
    removeOptimisticTicket: (ticketKey: string) => void;
    restoreOptimisticTicket: () => void;
    refreshedMetadata: Record<
      string,
      { status?: string; assignee?: string; resolution?: string }
    >;
    className?: string;
  }
>(function RelatedTickets(
  {
    risk,
    tickets,
    refetchTickets,
    removeOptimisticTicket,
    restoreOptimisticTicket,
    refreshedMetadata,
    className,
  },
  ref
) {
  const { currentTheme } = useTheme();
  const { mutateAsync: disconnectTicket } = useDisconnectTicket();

  // Derive ticketing config from AllIntegrations using provider name
  const getTicketIcon = (provider: string) => {
    const integration = Object.values(AllIntegrations).find(
      i => i.ticketing && i.ticketAttributeName === provider
    );
    return {
      icon: integration?.logo ?? 'jira.svg',
      label: integration?.name ?? 'Ticket',
    };
  };

  // Only show expanded state when there are multiple tickets
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const hasMultipleTickets = tickets.length > 1;

  const toggleTicket = (ticketId: string) => {
    if (hasMultipleTickets) {
      setExpandedTicketId(prev => (prev === ticketId ? null : ticketId));
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      refetchTickets();
    }, mToMs(1));

    return () => clearInterval(interval);
  }, [refetchTickets]);

  if (tickets.length === 0) {
    return null;
  }

  return (
    <div className={cn('h-fit shrink-0', 'w-[284px]', className)} ref={ref}>
      <div className="border-chariot-stroke bg-chariot-background-primary flex w-full flex-col border-l pl-6">
        <h1 className="pb-2 font-bold">Related Tickets</h1>
        <div className="flex flex-col overflow-hidden rounded-lg">
          {tickets?.map((ticket, index) => {
            const ticketKey = getTicketKey(ticket);
            const isExpanded =
              !hasMultipleTickets || expandedTicketId === ticket.id;
            const isLast = tickets.length - 1 === index;
            const isOptimistic = Boolean(ticket.isOptimistic);
            // Prefer refreshed metadata; fall back to embedded ticket fields
            const displayStatus =
              refreshedMetadata[ticketKey] !== undefined
                ? refreshedMetadata[ticketKey].status
                : ticket.status;
            const displayAssignee =
              refreshedMetadata[ticketKey]?.assignee ?? ticket.assignee;
            const displayResolution =
              refreshedMetadata[ticketKey]?.resolution ?? ticket.resolution;
            const displayLink =
              ticket.link && isValidExternalURL(ticket.link)
                ? ticket.link
                : undefined;
            const displayId = getTicketDisplayId(ticket);

            return (
              <div
                key={ticketKey}
                className={cn(
                  'border-chariot-stroke',
                  !isLast && 'border-b',
                  isExpanded &&
                    hasMultipleTickets &&
                    'bg-chariot-background-secondary',
                  // Add subtle pulse animation for optimistic placeholder
                  isOptimistic && 'animate-pulse opacity-70'
                )}
                data-testid={
                  isOptimistic ? 'optimistic-ticket' : 'related-ticket'
                }
              >
                <div
                  onClick={() => !isOptimistic && toggleTicket(ticket.id)}
                  className={cn(
                    'flex w-full items-center justify-between p-3 text-left',
                    isOptimistic ? 'cursor-default' : 'cursor-pointer'
                  )}
                  role="button"
                >
                  <div className="flex items-center gap-2">
                    {hasMultipleTickets && !isOptimistic && (
                      <ChevronDownIcon
                        className={`mr-1 size-4 shrink-0 stroke-[4px] transition-transform${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                    <img
                      src={`/icons/${currentTheme}/${getTicketIcon(ticket.provider).icon}`}
                      alt={`${getTicketIcon(ticket.provider).label} icon`}
                      className={cn('size-5', isOptimistic && 'opacity-50')}
                    />
                    <p className="text-sm font-bold text-nowrap">
                      {isOptimistic ? 'Creating Ticket' : 'Ticket ID'}
                    </p>
                    {isOptimistic ? (
                      // Show simple "Creating..." text for optimistic placeholder
                      <span className="text-chariot-text-secondary animate-pulse text-sm italic">
                        {ticket.id}
                      </span>
                    ) : displayLink ? (
                      // Direct link to ticket when link is available
                      <a
                        href={displayLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-chariot-brand-primary flex items-center gap-1 text-sm font-bold hover:underline"
                        aria-label={`Open ticket ${displayId} in new tab`}
                        data-testid="related-ticket-link"
                        onClick={e => e.stopPropagation()}
                      >
                        {displayId}
                        <ArrowTopRightOnSquareIcon className="size-3" />
                      </a>
                    ) : (
                      // Plain text when no link is available
                      <span className="text-sm font-bold">{displayId}</span>
                    )}
                  </div>
                  {/* Actions menu - always visible for non-optimistic tickets */}
                  {!isOptimistic && (
                    <JiraTicketActionsMenu
                      ticket={ticket}
                      onDisconnect={async () => {
                        // Optimistic removal: hide ticket immediately
                        removeOptimisticTicket(ticketKey);

                        try {
                          // Mutation with toast (toast already in hook)
                          await disconnectTicket({
                            riskKey: risk.key,
                            provider: ticket.provider,
                            ticketId: ticket.id,
                          });

                          // Invalidate risk queries so tickets list refreshes
                          queryClient.invalidateQueries({
                            queryKey: getQueryKey.genericSearch(''),
                          });

                          // Refetch tickets to get updated list
                          await refetchTickets();
                        } catch {
                          // Rollback optimistic removal on error
                          restoreOptimisticTicket();
                        }
                      }}
                    />
                  )}
                </div>
                {isExpanded && !isOptimistic && (
                  <div className="flex flex-col gap-2 p-3 pt-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold">Status</h3>
                      <DashedTag label={displayStatus || '-'} size="xs" />
                    </div>
                    {displayResolution && (
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold">Resolution</h3>
                        <DashedTag label={displayResolution} size="xs" />
                      </div>
                    )}
                    {displayAssignee && (
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold">Assignee</h3>
                        <span className="text-sm">{displayAssignee}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export function TicketModals() {
  const { mutateAsync: associateTicket } = useAssociateTicket();
  const jiraConfig = AllIntegrations['jira'];
  const { currentTheme } = useTheme();
  const {
    modal: { ticket },
  } = useGlobalState();
  const { data: accounts, status: accountStatus } = useUserSettings(
    undefined,
    'jira'
  );

  const [associatedTicketId, setAssociatedTicketId] = useState<string | null>(
    null
  );
  const [selectedAccountOption, setSelectedAccountOption] = useState<
    DropdownOption | undefined
  >();

  const JiraIcon = (
    <img
      src={`/icons/${currentTheme}/${jiraConfig?.logo}`}
      alt={jiraConfig.name}
      className="size-6"
    />
  );

  const accountsString = useMemo(() => JSON.stringify(accounts), [accounts]);

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (accounts?.length) {
      const timer = setTimeout(() => {
        setSelectedAccountOption({
          label: accounts[0].settings?.displayName ?? '',
          value: accounts[0].key,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [accountsString]);

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (ticket.associateTicket.open) {
      const timer = setTimeout(() => setAssociatedTicketId(null), 0);

      return () => {
        clearTimeout(timer);
        setAssociatedTicketId(null);
      };
    }
  }, [ticket.associateTicket.open]);

  return (
    <>
      <Modal
        baseClassName="overflow-visible"
        open={ticket.associateTicket.open}
        onClose={() => ticket.associateTicket.onOpenChange(false)}
        title={
          <div className="flex w-full flex-row items-center justify-between">
            <p>Associate Existing Ticket</p>
            <Select
              value={
                selectedAccountOption?.value !== undefined
                  ? String(selectedAccountOption.value)
                  : ''
              }
              onValueChange={value => {
                const account = accounts?.find(a => a.key === value);
                if (account) {
                  setSelectedAccountOption({
                    label: account.settings?.displayName ?? '',
                    value: account.key,
                  });
                }
              }}
            >
              <SelectTrigger className="max-w-[200px] gap-2 [&>span]:overflow-hidden [&>span]:text-ellipsis">
                <span className="flex items-center gap-2 overflow-hidden">
                  {JiraIcon}
                  <SelectValue placeholder="Select ticketing account" />
                </span>
              </SelectTrigger>
              <SelectContent className="z-200">
                {(accounts ?? [])
                  .filter(account => account.key !== '')
                  .map(account => (
                    <SelectItem key={account.key} value={account.key}>
                      <span className="flex items-center gap-2">
                        {/* TODO: In future when we have multiple integrations, update this to be dynamic */}
                        {JiraIcon}
                        {account.settings?.displayName ?? ''}
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        }
        footer={{
          text: 'Save',
          form: 'associate-ticket-form',
          disabled: accountStatus === 'pending',
        }}
      >
        <form
          id="associate-ticket-form"
          onSubmit={async e => {
            e.preventDefault();
            const promises = ticket.associateTicket?.riskKeys?.map(
              async riskKey => {
                await associateTicket({
                  account: String(selectedAccountOption?.value || ''),
                  riskKey,
                  ticketId: associatedTicketId || '',
                });
              }
            );
            await Promise.all(promises);

            // Invalidate queries (mark as stale)
            queryClient.invalidateQueries({
              queryKey: getQueryKey.genericSearch(''),
            });
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy('risk'),
            });

            // Immediately refetch active queries to update drawer
            await Promise.all([
              queryClient.refetchQueries({
                queryKey: getQueryKey.genericSearch(''),
                type: 'active',
              }),
              queryClient.refetchQueries({
                queryKey: getQueryKey.getMy('risk'),
                type: 'active',
              }),
            ]);

            ticket.associateTicket.onOpenChange(false);
          }}
        >
          <label className="flex flex-col gap-1">
            Ticket ID
            <InputText
              name="associatedTicketId"
              value={associatedTicketId || ''}
              onChange={e => setAssociatedTicketId(e.target.value)}
              placeholder="Enter existing ticket ID"
              required
            />
          </label>
        </form>
      </Modal>
      {ticket.setup.open && (
        <SetupModal
          isOpen={ticket.setup.open}
          onClose={() => ticket.setup.onOpenChange(false)}
          onComplete={() => ticket.setup.onOpenChange(false)}
          selectedIntegration={undefined}
          formData={{}}
          integration={AllIntegrations.jira}
        />
      )}
    </>
  );
}

export const CreateTicketModal = ({
  risk,
  onClose,
}: {
  risk: Risk;
  onClose: () => void;
}) => {
  const { currentTheme } = useTheme();

  // Fetch accounts for all ticketing integrations
  const { accounts: integrationAccounts, isLoading: isLoadingAccounts } =
    useTicketingAccounts();

  const { mutateAsync: createTicket, status: createTicketStatus } =
    useCreateTicket();

  const [selectedAccountOption, setSelectedAccountOption] = useState<
    DropdownOption | undefined
  >();
  const [step, setStep] = useState<
    'listTemplates' | 'configuration' | 'permissions'
  >('listTemplates');
  const [selectedTemplate, setSelectedTemplate] = useState<JiraTemplate>();
  // Identify accounts by `key` (always present + unique, e.g.
  // `#account#<tenant>#linear#...`) rather than `value`. Linear integration
  // accounts have an empty `value`, so matching on `value` failed to resolve
  // them and the dropdown could never select a Linear integration.
  const selectedAccount: Account | undefined = integrationAccounts?.find(
    account => account.key === selectedAccountOption?.value
  );
  const disabledAction =
    !selectedAccountOption ||
    isLoadingAccounts ||
    createTicketStatus === 'pending';

  const integrationIcon = (member: string) => {
    const config = getTicketingIntegration(member);
    return (
      <img
        src={`/icons/${currentTheme}/${config?.logo}`}
        alt={config?.name ?? 'Ticketing'}
        className="size-6"
      />
    );
  };

  const SelectedIcon = selectedAccount
    ? integrationIcon(selectedAccount.member)
    : integrationIcon('jira');

  const integrationAccountsString = useMemo(
    () => JSON.stringify(integrationAccounts),
    [integrationAccounts]
  );

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (integrationAccounts?.length) {
      const timer = setTimeout(() => {
        setSelectedAccountOption({
          label: integrationAccounts[0].settings?.displayName ?? '',
          value: integrationAccounts[0].key,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [integrationAccountsString]);

  async function handleCreateTicket() {
    const risks = [risk];
    const promises = risks.map(risk => {
      return createTicket({
        riskKey: risk.key,
        account: selectedAccount?.key ?? '',
        templateId: selectedTemplate?.id ?? '',
      });
    });
    await Promise.all(promises);

    // Invalidate queries (mark as stale)
    queryClient.invalidateQueries({
      queryKey: getQueryKey.genericSearch(''),
    });
    queryClient.invalidateQueries({
      queryKey: getQueryKey.getMy('risk'),
    });

    // Immediately refetch active queries to update drawer
    await Promise.all([
      queryClient.refetchQueries({
        queryKey: getQueryKey.genericSearch(''),
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: getQueryKey.getMy('risk'),
        type: 'active',
      }),
    ]);

    onClose();
  }

  return (
    <>
      {step === 'listTemplates' && (
        <Modal
          open={true}
          onClose={onClose}
          size="4xl"
          title={
            <div className="flex w-full flex-row items-center justify-between">
              <p>Select Integration and Project for Ticket Routing</p>
              <Select
                value={
                  selectedAccountOption?.value !== undefined
                    ? String(selectedAccountOption.value)
                    : ''
                }
                onValueChange={value => {
                  const account = integrationAccounts?.find(
                    a => a.key === value
                  );
                  if (account) {
                    setSelectedAccountOption({
                      label: account.settings?.displayName ?? '',
                      value: account.key,
                    });
                    setSelectedTemplate(undefined);
                  }
                }}
              >
                <SelectTrigger className="max-w-[200px] [&>span]:overflow-hidden [&>span]:text-ellipsis">
                  {/*
                    SelectValue echoes the selected SelectItem's content, which
                    already includes the integration icon + display name. Do NOT
                    render SelectedIcon here too — that produced a duplicate
                    ("double diamond") icon. The placeholder still shows when
                    nothing is selected.
                  */}
                  <SelectValue placeholder="Select integration" />
                </SelectTrigger>
                <SelectContent className="z-200">
                  {(integrationAccounts ?? [])
                    .filter(
                      (account): account is typeof account & { key: string } =>
                        typeof account.key === 'string' && account.key !== ''
                    )
                    .map(account => (
                      <SelectItem key={account.key} value={account.key}>
                        <span className="flex items-center gap-2">
                          {integrationIcon(account.member)}
                          {account.settings?.displayName ?? ''}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          }
          footer={{
            className: 'w-fit px-2',
            hideCancelButton: false,
            left: (
              <Button
                className="text-chariot-brand-primary border-none bg-transparent p-0 underline hover:bg-transparent"
                variant="ghost"
                onClick={() => {
                  setStep('configuration');
                }}
                disabled={disabledAction}
              >
                <PlusIcon className="size-4" />
                Add Project
              </Button>
            ),
            text: 'Confirm',
            onClick: handleCreateTicket,
            disabled: disabledAction || !selectedTemplate,
          }}
        >
          <Container>
            <CreateTicketTable
              data={selectedAccount}
              status={isLoadingAccounts ? 'pending' : 'success'}
              value={String(selectedAccountOption?.value || '')}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              icon={SelectedIcon}
              provider={selectedAccount?.member ?? 'jira'}
            />
          </Container>
        </Modal>
      )}
      {step === 'configuration' && (
        <SetupModal
          step="configuration"
          isOpen={true}
          onClose={() => setStep('listTemplates')}
          onComplete={() => setStep('listTemplates')}
          selectedIntegration={selectedAccount}
          formData={{
            displayName: selectedAccountOption?.label || '',
          }}
        />
      )}
    </>
  );
};

export const CreateTicketTable = ({
  data,
  status: statusProps,
  value,
  selectedTemplate,
  setSelectedTemplate,
  icon,
  provider = 'jira',
}: {
  data?: Account;
  value: string;
  status: 'pending' | 'error' | 'success';
  selectedTemplate?: JiraTemplate;
  setSelectedTemplate: (project: JiraTemplate) => void;
  icon: React.ReactNode;
  provider?: string;
}) => {
  const {
    templatesError,
    error,
    status: { isValidating, disconnectProjectStatus },
    onDisconnectTemplate,
    onValidateIntegration,
  } = useIntegrationSettings(provider, value, { enabled: false });
  const status =
    [statusProps, disconnectProjectStatus].includes('pending') || isValidating
      ? 'pending'
      : 'success';

  const className = 'group-[.is-disabled]:pointer-events-none';

  const dataString = useMemo(() => JSON.stringify({ data }), [data]);

  useEffect(() => {
    if (data && data.key) {
      onValidateIntegration({
        account: data.key,
        config: data.config,
        settings: data.settings,
      });
    }
  }, [dataString]);

  return (
    <Table
      name="projects"
      key={data?.key}
      rowClassName={row => {
        if (templatesError?.[row?.id]) {
          return 'text-chariot-stroke cursor-not-allowed group is-disabled';
        }
        return '';
      }}
      columns={[
        {
          id: '',
          label: '',
          fixedWidth: 30,
          cell: row => {
            if (!templatesError?.[row?.id]) {
              return null;
            }

            return (
              <InsufficientPermissionsTooltip
                template={row}
                templateError={templatesError[row.id]}
                onDisconnectTemplate={onDisconnectTemplate}
              />
            );
          },
        },
        {
          id: '',
          label: '',
          className,
          fixedWidth: 50,
          cell: row => {
            const checked = row.id === selectedTemplate?.id;
            return (
              <div
                className={cn(
                  'relative size-5 shrink-0 rounded-full border-2',
                  'flex items-center justify-center',
                  checked
                    ? 'border-chariot-brand-primary'
                    : 'border-chariot-stroke'
                )}
              >
                <div
                  className={cn(
                    'bg-chariot-brand-primary size-3 rounded-full transition-all duration-200',
                    checked ? 'scale-100' : 'scale-0'
                  )}
                />
              </div>
            );
          },
        },
        {
          id: '',
          label: 'Type',
          className,
          fixedWidth: 50,
          cell: () => icon,
        },
        {
          id: 'projectKey',
          label: 'Project Key',
          className,
          fixedWidth: 150,
          // Linear has no Jira-style project key — it routes by team, so show the
          // team key here. Jira keeps rendering the raw projectKey as before.
          cell:
            provider === 'linear'
              ? row => row.teamKey || '-'
              : row => row.projectKey || '-',
        },
        {
          id: 'projectName',
          label: 'Project Name',
          className,
          // Linear may have no project attached (project is optional); fall back
          // to the team name so the column isn't blank. Jira renders the raw
          // projectName as before.
          cell:
            provider === 'linear'
              ? row => row.projectName || row.teamName || '-'
              : row => row.projectName || '-',
        },
        // Linear has no issue-type concept, so the column is meaningless and is
        // omitted entirely for Linear. Jira/others keep rendering the raw
        // issueType.
        ...(provider === 'linear'
          ? []
          : ([
              {
                id: 'issueType',
                label: 'Issue Type',
                className,
                fixedWidth: 100,
                cell: (row: JiraTemplate) => row.issueType || '-',
              },
            ] satisfies Column<JiraTemplate>[])),
        {
          id: 'severities',
          label: 'Auto Ticket Creation',
          className,
          fixedWidth: 200,
          cell: row => {
            const options = generateSeverityOptions();
            const autoCreate = row?.autoCreate === 'true';

            const label = options.find(
              option => option.value === row.severities
            )?.label;
            return autoCreate ? label : '-';
          },
        },
      ]}
      onRowClick={row => {
        setSelectedTemplate(row);
      }}
      loadingRowCount={4}
      selection={{
        hide: true,
      }}
      data={(data?.settings?.templates ?? []).map(template => {
        return template as unknown as JiraTemplate;
      })}
      status={status}
      error={error}
    />
  );
};
